import {
  AdvancedFilterModel,
  ColDef,
  Column,
  GetDataPath,
  GridApi,
  ICellRendererParams,
  IServerSideGetRowsParams,
} from 'ag-grid-enterprise';
import { Observable } from 'rxjs';

import { InsertPosition } from '../services/dto/insert-position';
import { GridMetaData } from '../services/dto/metadata';
import { RowType } from '../services/dto/row-type';
import { FielOptions } from '../services/dto/view';
import { BaseCol, IColOptions } from './columns/base-col';

export interface IGridData<T extends { id: string }> {
  api: GridApi<T> | null;
  cols: { [index: string]: BaseCol<any> };
  filters: { forcedFilter?: IForcedFilter; searchText?: string };
  isTreeData: boolean;
  scope: string;
  getColumns(): Column<any>[] | null;
}

/** Instantané de la pagination AG Grid, publié sous forme de signal par `CamGridData`. */
export type GridPaginationState = {
  /** Index de page AG Grid, base 0. */
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalRows: number;
};

export type IGridColDef = ColDef & {
  metaData: GridMetaData;
};

export type ContentMenuActionType =
  | 'addChapter'
  | 'addComment'
  | 'addLine'
  | 'convertLine'
  | 'deleteLine'
  | 'duplicateTo'
  | 'hideLine'
  | 'moveTo'
  | 'selectForDuplicate'
  | 'selectForMove';

export interface IContentMenuSubActionConfig {
  disabled?: (data: any, node?: any) => boolean;
  icon?: string;
  name: string;
  position?: InsertPosition;
  targetType?: RowType;
}

export interface IContentMenuActionConfig {
  action: ContentMenuActionType;
  disabled?: (data: any, node?: any) => boolean;
  icon?: string | ((data: any, node?: any) => string);
  name: string | ((data: any, node?: any) => string);
  subMenu?: IContentMenuSubActionConfig[];
}

export type ContentMenuAction = 'separator' | 'openInExternalLink' | IContentMenuActionConfig;

export interface EditabledShortcut {
  action: ContentMenuActionType;
  /** `KeyboardEvent.code` (layout-independent, unaffected by the Alt modifier). */
  code: string;
  /** When true, the shortcut opens a position chord (arrow keys). */
  hasPosition?: boolean;
  /** Convert target type — only for `convertLine` variants. */
  targetType?: RowType;
}

export interface EditabledShortcutEvent {
  id: string;
  position: InsertPosition;
  shortcut: EditabledShortcut;
}

export enum FilterModelSource {
  view = 0,
  storage = 1,
  preSelected = 2,
}
export interface IFilterModelStorage {
  filterModel?: string;
  selectedFields?: string[];
  fieldOptions?: FielOptions[];
}
export interface IFieldsOverrides {
  [index: string]: ColDef & { colOptions?: IColOptions };
}
export interface ITreeData<T> {
  clientSide?: {
    getDataPath: GetDataPath<T>;
  };
  serverSide?: {
    isServerSideGroup?: (dataItem: T) => boolean;
    getServerSideGroupKey?: (dataItem: T) => string;
  };
  autoGroupColumnDef?: ColDef & {
    name: string;
    size: number;
    innerRenderer: (params: ICellRendererParams) => any;
    cellComponent?: any;
  };
}
export interface IDataService<T> {
  getChildren$: (params: IServerSideGetRowsParams<T>) => Observable<{ items: T[]; totalCount: number }>;
  getFieldsData$?: (search: string, colId: string) => Observable<string[]>;
}

export type IEditabledDataUpdateFields<T> = {
  id: string;
} & Partial<T>;

/** Une cellule editee : la valeur envoyee et celle a restaurer si l'enregistrement echoue. */
export interface EditabledCellEdit<T> {
  changes: IEditabledDataUpdateFields<T>;
  previous: IEditabledDataUpdateFields<T>;
}

export interface GridMutationUpdate<T> {
  modifiedProperties?: string[];
  row: T;
}

export interface GridMutationResult<T> {
  add?: T[];
  remove?: { id: string }[];
  update?: GridMutationUpdate<T>[];
}

export interface IEditabledDataService<T> {
  addLine$?: (type: RowType, relatedId?: string, position?: InsertPosition) => Observable<GridMutationResult<T>>;
  convertLine$?: (id: string, targetType: RowType) => Observable<GridMutationResult<T>>;
  deleteLine$?: (id: string) => Observable<GridMutationResult<T>>;
  duplicateLineTo$?: (
    sourceId: string,
    targetId: string,
    position: InsertPosition
  ) => Observable<GridMutationResult<T>>;
  getData$: (showHidden?: boolean) => Observable<{ items: T[] }>;
  getFieldsData$?: (search: string, colId: string) => Observable<string[]>;
  hideLine$?: (id: string) => Observable<GridMutationResult<T>>;
  moveLine$?: (id: string, targetId: string, position: InsertPosition) => Observable<GridMutationResult<T>>;
  updateField$: (fields: IEditabledDataUpdateFields<T>[]) => Observable<GridMutationResult<T>>;
}

export interface FooterEntry {
  key: string;
  value: number;
}

export interface IFooterDataService {
  getFooter$: () => Observable<FooterEntry[]>;
}

export interface IForcedFilter {
  [colId: string]: AdvancedFilterModel;
}

export interface IPreFilter {
  data: Record<string, unknown>;
  label: string;
}

/** `table` : vue tableau custom (`cam-grid-table`), pilotée par l'état AG Grid (colonnes, filtres, tri, pagination). */
export type GridViewType = 'grid' | 'card' | 'table';
